import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import {
  adminCloseMonth,
  adminIssueInvoice,
  adminPayInvoice,
  adminVoidInvoice,
  getOrgBilling,
} from "../api/dpp.js";
import { useAuth } from "../auth/AuthContext.js";
import { useWebConfig } from "../config/useWebConfig.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { formatUsd } from "../utils/money.js";
import type { Invoice, InvoiceStatus, OrgBillingView } from "../types.js";

const STATUS_COLOR: Record<InvoiceStatus, "default" | "warning" | "success" | "error"> = {
  draft: "default",
  issued: "warning",
  paid: "success",
  overdue: "error",
  void: "default",
};

/**
 * "Usage & billing" — the tenant sees this org's current-month view usage +
 * estimated amount and its invoice history. A platform admin (system:admin)
 * additionally gets the operator controls: close the current month and move an
 * invoice through its lifecycle (issue → pay, or void). Manual collection in v1.
 */
export function BillingPage() {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();
  const { hasPermission } = useAuth();
  const { billingEnabled } = useWebConfig();
  const { org, loading: orgsLoading } = useOrgs();

  const [data, setData] = useState<OrgBillingView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const isPlatformAdmin = hasPermission("system:admin");

  const load = async (orgId: string) => {
    setError(null);
    try {
      setData(await getOrgBilling(orgId));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    if (org) void load(org.id);
  }, [org?.id]);

  if (!billingEnabled) return <Alert type="info">{t("billing.disabled")}</Alert>;
  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !data) return <ErrorAlert error={error} onRetry={() => void load(org.id)} />;
  if (!data) return <Spin />;

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      await load(org.id);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const u = data.usage;
  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      {data.delinquent && <Alert type="error">{t("billing.delinquentNotice")}</Alert>}
      {data.viewLimitExceeded && <Alert type="warning">{t("billing.viewLimitNotice")}</Alert>}

      <Card title={t("billing.usageTitle")}>
        <Descriptions
          column={2}
          items={[
            {
              label: t("billing.plan"),
              children: <Tag color="primary">{t(`billingPlan.${u.billingPlan}`)}</Tag>,
            },
            { label: t("billing.annualFee"), children: formatUsd(u.annualFeeCents) },
            {
              label: `${t("billing.viewsThisYear")} (${u.year})`,
              children:
                u.includedViewsPerYear === null
                  ? `${u.viewsThisYear.toLocaleString()} · ${t("billing.unlimited")}`
                  : `${u.viewsThisYear.toLocaleString()} / ${u.includedViewsPerYear.toLocaleString()}`,
            },
            { label: t("billing.viewsThisMonth"), children: u.viewsThisMonth.toLocaleString() },
            {
              label: t("billing.products"),
              children:
                u.products.max === null
                  ? `${u.products.total.toLocaleString()} · ${t("billing.unlimited")}`
                  : `${u.products.total.toLocaleString()} / ${u.products.max.toLocaleString()}`,
            },
          ]}
        />
      </Card>

      {isPlatformAdmin && (
        <Card
          title={t("billing.adminTitle")}
          extra={
            <Button
              size="sm"
              loading={busy}
              onClick={() => void run(() => adminCloseMonth(), t("billing.monthClosed"))}
            >
              {t("billing.closeMonth")}
            </Button>
          }
        >
          <Text small secondary>
            {t("billing.adminHint")}
          </Text>
        </Card>
      )}

      <Card title={t("billing.invoicesTitle")}>
        <Table
          rowKey={(i) => i.id}
          empty={t("billing.noInvoices")}
          columns={[
            { key: "period", title: t("billing.period"), render: (i: Invoice) => i.period },
            {
              key: "kind",
              title: t("billing.kind"),
              render: (i: Invoice) => <Tag>{t(`invoiceKind.${i.kind}`)}</Tag>,
            },
            {
              key: "views",
              title: t("billing.views"),
              render: (i: Invoice) => (i.kind === "usage" ? i.views.toLocaleString() : "—"),
            },
            {
              key: "amount",
              title: t("billing.amount"),
              render: (i: Invoice) => formatUsd(i.amountCents),
            },
            {
              key: "status",
              title: t("common.status"),
              render: (i: Invoice) => (
                <Tag color={STATUS_COLOR[i.status]}>{t(`invoiceStatus.${i.status}`)}</Tag>
              ),
            },
            {
              key: "due",
              title: t("billing.due"),
              render: (i: Invoice) => (i.dueAt ? fmtDate(i.dueAt) : "—"),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (i: Invoice) =>
                isPlatformAdmin ? (
                  <Space size="sm">
                    {i.status === "draft" && (
                      <Button
                        size="sm"
                        loading={busy}
                        onClick={() => void run(() => adminIssueInvoice(i.id), t("billing.issued"))}
                      >
                        {t("billing.issue")}
                      </Button>
                    )}
                    {(i.status === "issued" || i.status === "overdue") && (
                      <Button
                        size="sm"
                        variant="primary"
                        loading={busy}
                        onClick={() => void run(() => adminPayInvoice(i.id), t("billing.markedPaid"))}
                      >
                        {t("billing.markPaid")}
                      </Button>
                    )}
                    {i.status !== "paid" && i.status !== "void" && (
                      <Popconfirm
                        title={t("billing.voidConfirm")}
                        okType="danger"
                        okText={t("billing.void")}
                        cancelText={t("common.cancel")}
                        onConfirm={() => void run(() => adminVoidInvoice(i.id), t("billing.voided"))}
                      >
                        <Button size="sm" variant="danger">
                          {t("billing.void")}
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                ) : null,
            },
          ]}
          data={data.invoices}
        />
      </Card>
    </Space>
  );
}
